import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Alert,
} from "@mui/material";
import { authService } from "../../../../services/auth.service";
import { settingsService } from "../../services/settings.service";
import { DiscountThresholdForm } from "../../types/pos.types";
import { hasPermission } from "../../../auth/utils/permission.utils";
import { useAuth } from "../../../auth/hooks/useAuth";

export const DiscountSettingsForm: React.FC = () => {
  const { user } = useAuth();
  const [values, setValues] = useState<DiscountThresholdForm>({
    cashierMaxPercentageDiscount: 0,
    cashierMaxFixedDiscount: 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Load user and thresholds on mount
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const user = await authService.getCurrentUser();
        setCurrentUser(user);
        console.log(user.business._id + " " + user.store_id);

        // Prefer business._id if available, fallback to store_id
        const store_id = user?.business?._id || user?.data.store_id;

        // ✅ Use service method
        const threshold = await settingsService.getDiscountThresholds({
          store_id,
        });

        setValues({
          cashierMaxPercentageDiscount:
            threshold.cashierMaxPercentageDiscount || 0,
          cashierMaxFixedDiscount: threshold.cashierMaxFixedDiscount || 0,
        });
      } catch (err: any) {
        if (err.message === "Discount threshold not found") {
          // 🟡 Show a soft warning
          setWarning(
            "No discount thresholds are configured yet. You can set them below."
          );
          // Reset values to zero
          setValues({
            cashierMaxPercentageDiscount: 0,
            cashierMaxFixedDiscount: 0,
          });
        } else {
          // 🔴 Real error
          setError(err.message || "Something went wrong");
        }
      }
    };

    loadUserData();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: parseFloat(value),
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    setWarning(null);

    try {
      const user = await authService.getCurrentUser();

      const store_id = user?.business?._id || user?.store_id;

      await settingsService.updateDiscountThresholds({
        store_id,
        cashierMaxPercentageDiscount: values.cashierMaxPercentageDiscount,
        cashierMaxFixedDiscount: values.cashierMaxFixedDiscount,
      });

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Discount Thresholds
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Paper sx={{ p: 3 }}>
        {/* ✅ Success */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Thresholds updated successfully!
          </Alert>
        )}

        {/* ⚠️ Warning – No thresholds exist yet */}
        {warning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {warning}
          </Alert>
        )}

        {/* ❌ Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Cashier Max % Discount"
          name="cashierMaxPercentageDiscount"
          value={values.cashierMaxPercentageDiscount}
          onChange={handleChange}
          fullWidth
          margin="normal"
          type="number"
          inputProps={{
            min: 0,
            step: 0.1,
          }}
        />
        <TextField
          label="Cashier Max Fixed Discount (BHD)"
          name="cashierMaxFixedDiscount"
          value={values.cashierMaxFixedDiscount}
          onChange={handleChange}
          fullWidth
          margin="normal"
          type="number"
          inputProps={{
            min: 0,
            step: 0.01,
          }}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={
            !hasPermission(
              "inventory.pos_configuration.update",
              user?.newPermissions,
              user?.assignment_details?.id,
              user?.is_store_owner
            )
          }
          sx={{ mt: 2 }}
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </Paper>
    </Box>
  );
};
